<template>
    <div class="zoezi-news-compose">
        <div v-if="!newsItem">
            <zoezi-user-avatar :user="$store.state.user" :size="32" class="mx-2" />
            <span class="news-title z-clickable" @click="editing = true">{{$translate('Click here to create a post')}}</span>
        </div>
        <v-dialog max-width="600px" v-model="editing" scrollable>
            <v-card>
                <v-card-title class="d-flex">
                    <span v-if="newsItem">{{$translate('Edit post')}}</span>
                    <span v-else>{{$translate('Create a post')}}</span>
                    <v-btn icon @click="editing = false" class="ml-auto"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <div>
                        <v-text-field
                            v-model="title"
                            :placeholder="$translate('Write a headline')"
                            :label="$translate('Headline')"
                            autofocus
                            @input="validateSlug"
                            >
                            <template #prepend>
                                <zoezi-user-avatar :user="$store.state.user" :size="32" class="mx-2" />
                            </template>
                        </v-text-field>
                        <div>
                            <v-text-field
                                v-model="slug"
                                :label="$translate('SEO friendly link')"
                                :disabled="!allowSlugEdit"
                                @input="onSlugInput"
                                :loading="slugStatus==='checking'"
                                :success="slugStatus==='ok'"
                                :error="slugStatus==='taken'"
                                :messages="slugMessages"
                                >
                                <template v-slot:append>
                                    <v-icon v-if="slugStatus=='checking'">mdi-loading</v-icon>
                                    <v-icon v-else-if="slugStatus=='ok' && slug">mdi-check</v-icon>
                                    <v-icon v-else-if="slugStatus=='taken'">mdi-alert-circle</v-icon>
                                    <v-btn v-if="!userTouchedSlug && !allowSlugEdit" color="primary" text @click="allowSlugEdit = !allowSlugEdit">
                                        {{ $translate('Change') }}
                                    </v-btn>

                                    <v-btn v-if="userTouchedSlug" icon @click="resetSlug" :disabled="!title">
                                        <v-icon>mdi-refresh</v-icon>
                                    </v-btn>
                                </template>
                            </v-text-field>
                        </div>
                    </div>
                    <v-auto-img v-if="imagekey" :src="imagekey" max-height="200" class="white--text mb-2 mt-1">
                        <v-btn right icon @click="imagekey = null" class="ml-auto d-flex"><v-icon class="white--text">mdi-close</v-icon></v-btn>
                    </v-auto-img>
                    <v-row v-else class="mt-1">
                        <v-col cols=12>
                            <v-btn block outlined color="primary" @click="showMediaLibrary = true"><v-icon left>mdi-image</v-icon>{{$translate('Choose image')}}</v-btn>
                        </v-col>
                    </v-row>
                    <editor v-if="editing" :placeholder="$translate('Write a content for the news')" :bubble="true" v-model="content" style="min-height: 300px" />
                    <!-- Not using zoezi-autocomplete here because of issues due to value being an array -->
                    <v-autocomplete multiple :items="categoryItems" :label="$translate('Categories')" item-text="name" item-value="id" v-model="publishCategories" :placeholder="$translate('Choose category')" :search-input.sync="categorySearchInput">
                        <template #no-data><span></span>
                        </template>
                        <template #append-item>
                            <v-list-item @click="addCategory(categorySearchInput)" v-if="categorySearchInput && !categoryItems.find(x => x.name == categorySearchInput)">
                                {{$translate('Add category')}}: {{categorySearchInput}}
                            </v-list-item>
                            <v-list-item v-else-if="!categorySearchInput && categoryItems.length == 0">
                                {{$translate('Write the name of the category')}}
                            </v-list-item>
                        </template>
                    </v-autocomplete>
                    <label>{{$translate('Publish at time')}}</label>
                    <v-row no-gutters>
                        <v-col md=6 cols=12>
                            <date-field :label="$translate('Date')" prepend-icon="mdi-calendar" v-model="published.date" @input="published.date = $event">
                            </date-field>
                        </v-col>
                        <v-col md=6 cols=12>
                            <time-field :label="$translate('Time')" prepend-icon="mdi-clock" v-model="published.time" @input="published.time = $event" >
                            </time-field>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" block :disabled="!canPublish" @click="publish" :loading="loading">{{$translate('Publish')}}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="showMediaLibrary"
            max-width="90%"
            scrollable
        >
            <v-card>
                <v-card-title class="headline zoezi-edit-color">
                    {{$translate('Choose image')}}
                    <v-btn icon @click="showMediaLibrary = false" class="ml-auto zoezi-edit-color"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <media-library :columns="$vuetify.breakpoint.mobile ? 1 : 6" type="image" @change="imageSelected" />
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import MediaLibrary from '../components/MediaLibrary.vue'
import Editor from '../components/Editor.vue'
import ZoeziUserAvatar from '../components/UserAvatar.vue'
import DateField from './DateField.vue'
import TimeField from './TimeField.vue'
import { makeValidSlugFromTitle } from '../util.js'
import { debounce } from 'lodash'

export default {
    name: 'zoezi-news-compose',

    components: {
        MediaLibrary,
        Editor,
        ZoeziUserAvatar,
        DateField,
        TimeField,
    },

    props: {
        categories: {
            type: Array
        },
        newsItem: {
            type: Object
        }
    },

    computed: {
        canPublish() {
            return this.title && this.content && this.publishCategories?.length;
        },
        slugMessages () {
            const hint = this.$translate('Link is used in the URL of the post');
            if (this.slugStatus == 'taken') return [`${this.slugMsg}. ${hint}`]
            if (this.slugStatus == 'ok' && this.slug) return [`${this.slugMsg}. ${hint}`]
            return [hint]
        }
    },

    watch: {
        categories: {
            immediate: true,
            handler() {
                this.publishCategories = this.categories?.filter(x => x);
            }
        },
        newsItem: {
            immediate: true,
            handler() {
                if (this.newsItem) {
                    this.editing = true;
                    this.imagekey = this.newsItem.imagekey;
                    this.title = this.newsItem.title;
                    this.content = this.newsItem.content;
                    this.publishCategories = this.newsItem.categories?.concat();
                    this.slug = this.newsItem.slug;
                    let pub = this.newsItem.published?.split(' ');
                    if (pub) {
                        this.published.date = pub[0];
                        this.published.time = pub[1];
                    }

                }

            }
        },
        title(newTitle) {
            if (!this.userTouchedSlug && newTitle) {
                this.slug = makeValidSlugFromTitle(newTitle);
            }
        },
        editing() {
            if (!this.editing) {
                this.$emit('close');
                this.reset();
            }
        }
    },

    data: () => ({
        categoryItems: [],
        editing: false,
        loading: false,
        publishCategories: [],
        title: '',
        content: '',
        allowSlugEdit: false,
        slug: '',
        imagekey: null,
        showMediaLibrary: false,
        categorySearchInput: '',
        published: {
            date: '',
            time: ''
        },
        userTouchedSlug: false,
        slugValid: false,
        slugStatus: 'idle',
        slugMsg: ''
    }),

    methods: {
        validateSlug: debounce(function () {
            if (!this.slug) return
            this.slugStatus = 'checking'
            this.$api.get('/api/news/item/slug/validate', { slug: this.slug }).then(res => {
                const taken = res.exists && (!this.newsItem || res.key != this.newsItem.key);
                if (taken) {
                    this.allowSlugEdit = true;
                    this.slugStatus = 'taken';
                    this.slugMsg = this.$translate('Link is already used in another post, please change the URL');
                } else {
                    this.slugStatus = 'ok';
                    this.slugMsg = this.$translate('Link is available');
                }
            })
            .catch(() => {
                this.slugStatus = 'idle';
                this.slugMsg = this.$translate('Could not validate Link right now');
            })
        }, 500),
        resetSlug() {
            this.slug = makeValidSlugFromTitle(this.title);
            this.userTouchedSlug = false;
            this.allowSlugEdit = false;
            this.validateSlug();
        },
        makeValidSlugFromTitle(title) {
            return makeValidSlugFromTitle(title);
        },
        onSlugInput() {
            this.userTouchedSlug = true;
            this.slug = makeValidSlugFromTitle(this.slug);
            this.validateSlug();
        },
        publish() {
            this.loading = true;
            let pub = [this.published.date || Date.today().yyyymmdd(), this.published.time].filter(Boolean).join(' ');

            let item = {
                title: this.title,
                imagekey: this.imagekey,
                content: this.content,
                categories: this.publishCategories,
                published: pub,
                slug: this.slug
            };

            if (this.newsItem) {
                item.id = this.newsItem.id;

                this.$api.post('/api/news/item/change', item).then(response => {
                    this.loading = false;

                    if (!response.ok) {
                        response.json().then((data) => {
                            const text = data?.message ?? 'It was not possible to change the post';
                            this.$store.commit('showErrorDialog', {title: this.$translate('Changing post failed'), text: this.$translate(text)});
                        })
                    } else {
                        this.editing = false;
                        this.reset();
                    }
                })
            } else {
                this.$api.post('/api/news/item/add', item).then(response => {
                    this.loading = false;

                    if (!response.ok) {
                        response.json().then((data) => {
                            const text = data?.message ?? 'It was not possible to create the post';
                            this.$store.commit('showErrorDialog', {title: this.$translate('Creating post failed'), text: this.$translate(text)});
                        })
                    } else {
                        this.editing = false;
                        this.reset();
                    }
                })
            }
        },
        imageSelected(image) {
            this.imagekey = image;
            this.showMediaLibrary = false;
        },
        getCategories() {
            return this.$api.get('/api/news/category/get').then(categories => {
                return this.categoryItems = categories.sort((a,b) => a.name.localeCompare(b.name));
            });
        },
        addCategory(name) {
            this.$api.post('/api/news/category/add', {name}).then(() => {
                this.getCategories().then(() => {
                    let item = this.categoryItems.find(x => x.name == name);
                    if (item) {
                        if (!this.publishCategories) {
                            this.publishCategories = [];
                        }
                        this.publishCategories.push(item.id);
                    }

                    this.categorySearchInput = '';
                })
            });
        },
        reset() {
            this.title = '';
            this.imagekey = null;
            this.content = '';
            this.publishCategories = this.categories?.filter(x => x)
            this.published.date = null;
            this.published.time = null;
            this.slug = '';
            this.userTouchedSlug = false;
            this.allowSlugEdit = false;
        }
    },

    mounted() {
        this.getCategories();
    }
}
</script>
